import { useEffect, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { getSource } from 'selectors/offerSelectors'
import usePrevious from 'hooks/usePrevious'
import { SOURCE_TYPES } from 'constants/source'

const B2C_METASEARCH_SOURCE_TYPES: ReadonlyArray<string> = [SOURCE_TYPES.METASEARCH, SOURCE_TYPES.METASEARCH_SESSION]

// Segment-exact match: source is a comma-joined string, so split before checking.
// A substring check would also match names that merely contain "metasearch"
// (e.g. metasearchPriceVerifier), misreading a CUG session as B2C metasearch.
const isB2cMetasearchSource = (source?: string): boolean =>
  !!source?.split(',').some(sourceType => B2C_METASEARCH_SOURCE_TYPES.includes(sourceType))

/**
 * This hook monitors the transition from a metasearch source to a non-metasearch source.
 * If such a transition occurs, use the result of this hook to refetch CUG rates
 * instead of displaying B2C rates for bedbanks.
*/
const useMetasearchSourceTransition = () => {
  const [hasTransitionedFromMetasearch, setHasTransitionedFromMetasearch] = useState(false)
  const currentSource = useAppSelector(getSource)
  const previousSource = usePrevious(currentSource)

  useEffect(() => {
    const wasMetasearch = isB2cMetasearchSource(previousSource)
    const isNoLongerMetasearch = !isB2cMetasearchSource(currentSource)
    const isMetasearch = isB2cMetasearchSource(currentSource)

    if (wasMetasearch && isNoLongerMetasearch) {
      setHasTransitionedFromMetasearch(true)
    } else if (isMetasearch) {
      setHasTransitionedFromMetasearch(false)
    }
  }, [previousSource, currentSource])

  return hasTransitionedFromMetasearch
}

export default useMetasearchSourceTransition
